import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Globe } from "lucide-react";
import { Logo } from "@/components/Reveal";
import { scrollToSection } from "@/lib/smoothScroll";
import { useLang } from "@/context/LanguageContext";

const LINKS = [
  { hash: "#home", key: "home", testid: "nav-link-home" },
  { hash: "#chi-sono", key: "chiSono", testid: "nav-link-chi-sono" },
  { hash: "#come-funziona", key: "comeFunziona", testid: "nav-link-come-funziona" },
  { hash: "#percorso", key: "percorso", testid: "nav-link-percorso" },
  { hash: "#risultati", key: "risultati", testid: "nav-link-risultati" },
  { hash: "#news", key: "news", testid: "nav-link-news" },
  { hash: "#gallery", key: "gallery", testid: "nav-link-momenti" },
  { hash: "#sponsor", key: "sponsor", testid: "nav-link-sponsor" },
  { hash: "#collaborazioni", key: "collaborazioni", testid: "nav-link-collaborazioni" },
  { hash: "#contatti", key: "contatti", testid: "nav-link-contatti" },
];

function LangToggle({ mobile = false }) {
  const { lang, setLang, t } = useLang();
  return (
    <span
      role="group"
      aria-label={t("lang.label")}
      className={`inline-flex items-center gap-1 rounded-full border border-slate-700 bg-slate-900/60 p-1 ${mobile ? "" : "backdrop-blur-md"}`}
    >
      {!mobile && <Globe className="ml-1.5 h-3.5 w-3.5 text-slate-500" />}
      {["it", "en"].map((l) => (
        <button
          key={l}
          data-testid={`lang-toggle-${l}${mobile ? "-mobile" : ""}`}
          onClick={() => setLang(l)}
          aria-pressed={lang === l}
          className={`rounded-full px-2.5 py-1 text-[10px] font-bold tracking-[0.15em] transition-colors duration-300 ${
            lang === l ? "bg-amber-400 text-[#090D14]" : "text-slate-400 hover:text-slate-100"
          }`}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </span>
  );
}

export default function Navbar() {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (hash) => {
    setOpen(false);
    setTimeout(() => scrollToSection(hash), open ? 250 : 0);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled || open ? "bg-[#090D14]/85 backdrop-blur-xl border-b border-slate-800/70" : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[70px] flex items-center justify-between">
        <button onClick={() => go("#home")} aria-label="Torna all'inizio" className="focus:outline-none">
          <Logo />
        </button>

        <ul className="hidden lg:flex items-center gap-7">
          {LINKS.slice(1).map((l) => (
            <li key={l.hash}>
              <button
                data-testid={l.testid}
                onClick={() => go(l.hash)}
                className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-300 hover:text-amber-400 transition-colors duration-300"
              >
                {t(`nav.${l.key}`)}
              </button>
            </li>
          ))}
          <li>
            <LangToggle />
          </li>
        </ul>

        <div className="flex items-center gap-3 lg:hidden">
          <LangToggle mobile />
          <button
            data-testid="nav-hamburger-btn"
            onClick={() => setOpen(!open)}
            className="text-slate-200 p-2"
            aria-label="Apri menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-[#090D14]/95 backdrop-blur-xl border-b border-slate-800/70"
          >
            <ul className="px-6 py-6 space-y-1">
              {LINKS.map((l, i) => (
                <motion.li
                  key={l.hash}
                  initial={{ opacity: 0, x: -18 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.4 }}
                >
                  <button
                    data-testid={`${l.testid}-mobile`}
                    onClick={() => go(l.hash)}
                    className="font-display w-full text-left text-3xl tracking-[0.08em] text-slate-100 hover:text-amber-400 transition-colors py-2"
                  >
                    {t(`nav.${l.key}`)}
                  </button>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
